import { Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import MainLayout from "./components/layout/MainLayout";
import ProtectedRoute from "./components/common/ProtectedRoute";
import PublicRoute from "./components/common/PublicRoute";
import LoadingSpinner from "./components/common/LoadingSpinner";
import { AuthProvider } from "./features/users/context/AuthContext";
import { Toaster } from "react-hot-toast";
import UserForm from "./features/users/components/UserForm";
import { PageProvider } from "@/features/context/PageContext";
import ShopList from "./features/shops/components/ShopList";
import ShopDetail from "./features/shops/components/ShopDetail";
import ProductList from "./features/products/components/ProductList";
import LiveList from "./features/lives/components/LiveList";
import LoyaltyList from "./features/loyalty/components/LoyaltyList";
import PlanList from "./features/plans/components/PlanList";
import ShopSubscriptionList from "./features/shops/components/ShopSubscriptionList";
import AdvertisementList from "./features/advertisements/components/AdvertisementList";
import StoryList from "./features/stories/components/StoryList";
import WithdrawalList from "./features/withdrawals/components/WithdrawalList";
import DeliveryList from "./features/deliveries/components/DeliveryList";
import ProductRequestList from "./features/productRequests/components/ProductRequestList";
import ReportList from "./features/reports/components/ReportList";
import OrderList from "./features/orders/components/OrderList";
import PaymentList from "./features/paiements/components/PaymentHistory";
import PaymentStatistics from "./features/paiements/components/PaymentStatistics";
import CategoryList from "./features/categories/components/CategoryList";
import SettingsList from "./features/settings/components/SettingsList";
import LogsList from "./features/settings/components/LogsList";
import ServiceList from "./features/services/components/ServiceList";
import ServiceDetails from "./features/services/components/ServiceDetails";
import ServiceSubscriptionList from "./features/services/components/ServiceSubscriptionList";
import ServiceCategoryList from "./features/services/components/ServiceCategoryList";
import AppVersionList from "./features/app-versions/components/AppVersionList";

// Import lazy pour les pages principales
const Login = lazy(() => import("./features/auth/components/Login"));
const Dashboard = lazy(() =>
  import("./features/dashboard/components/Dashboard")
);
const UserList = lazy(() => import("./features/users/components/UserList"));
const UserDetail = lazy(() => import("./features/users/components/UserDetail"));
const AdminList = lazy(() => import("./features/admins/components/AdminList"));
const MarketingCenter = lazy(() => import("./features/marketing/components/MarketingCenter"));

function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AuthProvider>
        <PageProvider>
          <Routes>
            {/* Routes publiques */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />

            {/* Routes protégées */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Gestion des utilisateurs */}
              <Route path="/users" element={<UserList />} />

              {/* Gestion des administrateurs */}
              <Route path="/admins" element={<AdminList />} />

              <Route path="/shops" element={<ShopList />} />
              <Route path="/shops/partnerships" element={<ShopSubscriptionList />} />
              <Route path="/shops/:id" element={<ShopDetail />} />
              <Route path="/all/products" element={<ProductList />} />
              <Route path="/lives" element={<LiveList />} />
              <Route path="/loyalty" element={<LoyaltyList />} />
              <Route path="/plans" element={<PlanList />} />

              <Route path="/orders" element={<OrderList />} />
              <Route path="/marketing" element={<MarketingCenter />} />
              <Route path="/paiements" element={<PaymentList />} />
              <Route path="/stats/paiements" element={<PaymentStatistics />} />
              <Route path="/withdrawals" element={<WithdrawalList />} />
              <Route path="/deliveries" element={<DeliveryList />} />
              <Route path="/product-requests" element={<ProductRequestList />} />
              <Route path="/categories" element={<CategoryList />} />
              <Route path="/settings" element={<SettingsList />} />
              <Route path="/app-versions" element={<AppVersionList />} />
              <Route path="/logs" element={<LogsList />} />

              <Route path="/advertisements" element={<AdvertisementList />} />
              <Route path="/stories" element={<StoryList />} />
              <Route path="/reports" element={<ReportList />} />
              <Route path="/services" element={<ServiceList />} />
              <Route path="/services/categories" element={<ServiceCategoryList />} />
              <Route path="/services/:id" element={<ServiceDetails />} />
              <Route
                path="/services/subscriptions"
                element={<ServiceSubscriptionList />}
              />
            </Route>

            {/* Route 404 */}
            {/* <Route path="*" element={<NotFound />} /> */}
          </Routes>
        </PageProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: "#363636",
              color: "#fff",
            },
            success: {
              duration: 3000,
              theme: {
                primary: "green",
                secondary: "black",
              },
            },
          }}
        />
      </AuthProvider>
    </Suspense>
  );
}

export default App;
